import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { PaymentIcon, enabledPaymentMethods, paymentMethodFields, } from '../_shared/paymentIcons.js';
/**
 * Shrine `payment-badges` — a row of payment-method cards (Visa, Mastercard, American Express, PayPal,
 * Apple Pay, Google Pay, cash on delivery, …) under a "100% secure payment" caption.
 *
 * ONE SWITCH PER METHOD (founder's request, 2026-10-05). Shrine's `enabled_payment_types` was a free-text
 * list — our `enabledPaymentTypes` reproduced it, and it is the one control in the block no seller was
 * going to use: she does not know the alias for Apple Pay, and nothing on screen tells her which names
 * exist. The field is gone from the inspector; in its place, the «Payment methods» group lists every
 * card the catalog can paint with a switch each. The drawings and the list live in `_shared/paymentIcons`,
 * shared with the footer's payment row so the two strips can never disagree on what Visa looks like.
 *
 * A strip saved with the old free text keeps painting exactly what it painted: `enabledPaymentMethods`
 * still reads `enabledPaymentTypes` for any switch the seller has not touched yet.
 *
 * Shrine's other settings are reproduced as before:
 *  - `visibility`           → visibilityField (All devices / Mobile only / Desktop only).
 *  - `width_desktop`        → `widthDesktop` (range 26–50 step 2 px, default 38) — card width on ≥750px.
 *  - `width_mobile`         → `widthMobile`  (range 20–44 step 2 px, default 38) — card width on <750px.
 *  - `margin_top`/`margin_bottom` → marginFields (0–45 step 3, defaults 15/15).
 * Plus `alignment` (left / center / right; Shrine hard-centres) and `caption` (dictionary-backed).
 */
export const paymentBadgesBlockSchema = {
    type: 'payment-badges',
    name: 'Payment badges',
    max: 1,
    fields: [
        visibilityField,
        {
            id: 'caption',
            type: 'text',
            label: 'Caption',
            info: 'Reassurance line shown above the badges. Leave empty to hide.',
            default: 'Paiement 100% sécurisé',
            defaultKey: 'product.secure_payment_100',
            maxLength: 80,
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Alignment',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        // ── Payment methods — one switch per card ──────────────────────────────────
        ...paymentMethodFields('Payment methods'),
        // ── Badges size ────────────────────────────────────────────────────────────
        {
            id: 'widthDesktop',
            type: 'range',
            label: 'Badges width (desktop)',
            group: 'Badges size',
            min: 26,
            max: 50,
            step: 2,
            default: 38,
            unit: 'px',
        },
        {
            id: 'widthMobile',
            type: 'range',
            label: 'Badges width (mobile)',
            group: 'Badges size',
            min: 20,
            max: 44,
            step: 2,
            default: 38,
            unit: 'px',
        },
        // ── Margins ──────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
/**
 * "100% secure payment" caption + a centred (configurable) row of payment cards.
 * SSR-safe: pure markup + one scoped <style> driving the responsive card width (desktop @container
 * ≥750px, mobile default) — degrades fully without JS.
 */
const PaymentBadgesBlock = ({ block, context }) => {
    // Payment badges are pure trust chrome — they read ONLY their own settings (no product data), so they render
    // in ANY context: the product buy-box, the footer, AND the cart drawer (feature 0030). No product-state gate.
    const bs = block.settings;
    const cls = `aqx-pay-${block.id}`;
    const methods = enabledPaymentMethods(bs);
    const caption = str(bs.caption, t(context, 'product.secure_payment_100'));
    const justify = JUSTIFY[str(bs.alignment, 'center')] ?? 'center';
    const wDesktop = num(bs.widthDesktop, 38);
    const wMobile = num(bs.widthMobile, 38);
    if (methods.length === 0)
        return null;
    // Mobile-first base width; desktop override via `@container aqx-page` ONLY (always present via
    // PageRenderer) — a paired @media(min-width) would fire off the builder's wide editor window inside a
    // narrow mobile preview.
    const css = `.${cls}__badge{display:inline-flex;width:${wMobile}px}` +
        `.${cls}__badge svg{width:100%;height:auto}` +
        `@container aqx-page (min-width:750px){.${cls}__badge{width:${wDesktop}px}}` +
        visibilityCss(cls, bs.visibility);
    const wrap = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: justify === 'center' ? 'center' : justify === 'flex-end' ? 'flex-end' : 'flex-start',
        gap: 'var(--aqx-theme-space-2, 8px)',
        textAlign: justify === 'center' ? 'center' : justify === 'flex-end' ? 'right' : 'left',
        ...marginStyle(bs),
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { className: cls, style: wrap, children: [caption ? (_jsx("span", { style: {
                            fontSize: 'var(--aqx-theme-text-sm, 13px)',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: 'var(--aqx-scheme-muted)',
                        }, children: caption })) : null, _jsx("div", { style: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: justify,
                            alignItems: 'center',
                            gap: '6px',
                            width: '100%',
                        }, children: methods.map((m) => (_jsx("span", { className: `${cls}__badge`, "data-payment": m.key, children: _jsx(PaymentIcon, { method: m, label: m.labelKey ? t(context, m.labelKey) : m.label }) }, m.key))) })] })] }));
};
export const paymentBadgesBlock = {
    type: 'payment-badges',
    schema: paymentBadgesBlockSchema,
    Component: PaymentBadgesBlock,
    meta: { label: 'Payment badges' },
};
//# sourceMappingURL=index.js.map